import type { CSSProperties } from "react";

/**
 * Orange bubble (Clawpify “Join waitlist”): same stops as
 * `linear-gradient(rgb(255,232,206) 0%, … rgb(234,88,12) 88%)` plus a deep base at 100%.
 */
const landingAccentOrange = {
  gradient:
    "linear-gradient(180deg, #ffe8ce 0%, #ffb366 22%, #ff8c28 52%, #ea580c 88%, #c2410c 100%)",
  rim: "#9a3412",
  insetHighlight: "rgba(255, 255, 255, 0.65)",
  insetShadow: "rgba(120, 40, 0, 0.18)",
} as const;

/** Photobomb accent: same glossy chrome treatment, violet ramp for dark UI */
const photobombAccentViolet = {
  gradient:
    "linear-gradient(180deg, #f3e8ff 0%, #d8b4fe 22%, #a78bfa 52%, #7c3aed 88%, #5b21b6 100%)",
  rim: "#4c1d95",
  insetHighlight: "rgba(255, 255, 255, 0.55)",
  insetShadow: "rgba(76, 29, 149, 0.22)",
} as const;

export const SurfaceStyle: CSSProperties = {
  background: landingAccentOrange.gradient,
  borderColor: landingAccentOrange.rim,
  boxShadow: [
    `inset 0 1px 0 0 ${landingAccentOrange.insetHighlight}`,
    `inset 0 -1px 0 0 ${landingAccentOrange.insetShadow}`,
    "0 1px 0 rgba(255, 255, 255, 0.35)",
    "0 6px 14px rgba(154, 52, 18, 0.28)",
    "0 2px 4px rgba(0, 0, 0, 0.08)",
  ].join(", "),
};

/** Same bubble chrome as orange; swap ramp to match Photobomb marketing on dark backgrounds */
export const SurfaceStylePhotobomb: CSSProperties = {
  background: photobombAccentViolet.gradient,
  borderColor: photobombAccentViolet.rim,
  boxShadow: [
    `inset 0 1px 0 0 ${photobombAccentViolet.insetHighlight}`,
    `inset 0 -1px 0 0 ${photobombAccentViolet.insetShadow}`,
    "0 1px 0 rgba(255, 255, 255, 0.3)",
    "0 8px 20px rgba(91, 33, 182, 0.35)",
    "0 2px 6px rgba(0, 0, 0, 0.2)",
  ].join(", "),
};

export const SurfaceClassName = [
  "relative isolate overflow-hidden rounded-full",
  "border font-medium antialiased text-white shadow-md",
  "transition-[filter,opacity]",
  "before:pointer-events-none before:absolute before:inset-x-[12%] before:top-px before:z-[1] before:h-[42%] before:rounded-[999px]",
  "before:bg-gradient-to-b before:from-white/50 before:via-white/15 before:to-transparent",
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:top-[55%] after:rounded-b-full",
  "after:bg-gradient-to-t after:from-black/10 after:to-transparent",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151519]",
  "active:brightness-[0.97]",
  "disabled:cursor-not-allowed disabled:opacity-45",
].join(" ");

/** Glossy shell paired with {@link SurfaceStylePhotobomb} (violet focus ring for dark pages) */
export const SurfaceClassNamePhotobomb = [
  "relative isolate overflow-hidden rounded-full",
  "border font-medium antialiased text-white shadow-md",
  "transition-[filter,opacity]",
  "before:pointer-events-none before:absolute before:inset-x-[12%] before:top-px before:z-[1] before:h-[42%] before:rounded-[999px]",
  "before:bg-gradient-to-b before:from-white/50 before:via-white/15 before:to-transparent",
  "after:pointer-events-none after:absolute after:inset-x-0 after:bottom-0 after:top-[55%] after:rounded-b-full",
  "after:bg-gradient-to-t after:from-black/10 after:to-transparent",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#151519]",
  "active:brightness-[0.97]",
  "disabled:cursor-not-allowed disabled:opacity-45",
].join(" ");
